import { httpJsonError } from "./httpJsonError";
/**
 * nativeHttp.js
 *
 * Thin wrapper that routes HTTP requests through the Capacitor native layer
 * (URLSession on iOS) when running inside an iOS WKWebView.  This bypasses
 * the WKWebView fetch/XHR stack that blocks requests to firestore.googleapis.com
 * with "TypeError: Load failed" / status=0.
 *
 * On Android and Web the standard fetch() API is used unchanged.
 */

// CapacitorHttp is part of @capacitor/core >= 4 – no extra dependency needed.
import { Capacitor, CapacitorHttp } from "@capacitor/core";

/**
 * Returns true when the app is running inside an iOS Capacitor WebView.
 * Exported so callers can use the same detection logic without duplicating it.
 */
export function getCapacitorRuntimeDebug() {
  try {
    const importedPlatform =
      typeof Capacitor?.getPlatform === "function"
        ? Capacitor.getPlatform()
        : (Capacitor?.platform || null);
    const importedIsNative =
      typeof Capacitor?.isNativePlatform === "function"
        ? Capacitor.isNativePlatform()
        : null;
    const windowPlatform =
      typeof window !== "undefined" && window.Capacitor
        ? (typeof window.Capacitor.getPlatform === "function"
            ? window.Capacitor.getPlatform()
            : window.Capacitor.platform || null)
        : null;
    const hasNativeBridge = Boolean(
      typeof window !== "undefined" &&
      (window.Capacitor || window.webkit?.messageHandlers?.bridge || window.webkit?.messageHandlers?.capacitor)
    );
    return {
      importedPlatform: importedPlatform || null,
      windowPlatform: windowPlatform || null,
      importedIsNative,
      hasNativeBridge,
      userAgent: typeof navigator !== "undefined" ? String(navigator.userAgent || "") : "",
    };
  } catch (e) {
    return {
      importedPlatform: null,
      windowPlatform: null,
      importedIsNative: null,
      hasNativeBridge: false,
      error: e?.message || String(e),
    };
  }
}

/**
 * Robust iOS Capacitor detection.
 *
 * V16D: do not rely only on window.Capacitor. In TestFlight/iOS builds the
 * imported Capacitor runtime can know the platform even when the global bridge
 * object is not yet available at module evaluation time. If this returns false
 * inside iOS, the app falls back to Web SDK paths and the worker agenda can stay
 * on "Cargando tu agenda" without ever calling the Cloud Function.
 */
export function isIosCapacitor() {
  try {
    const debug = getCapacitorRuntimeDebug();
    const platform = debug.importedPlatform || debug.windowPlatform || "";
    return String(platform).toLowerCase() === "ios";
  } catch (e) {
    return false;
  }
}

/**
 * httpRequestJson({ url, method, headers, params, data })
 *
 * Makes an HTTP request and returns the parsed JSON response body.
 *
 * @param {Object}  options
 * @param {string}  options.url     - Full URL (without query string).
 * @param {string}  [options.method="GET"]
 * @param {Object}  [options.headers={}]
 * @param {Object}  [options.params=null] - Query-string parameters (key/value).
 * @param {*}       [options.data=null]   - Request body (will be JSON-serialised on web).
 * @returns {Promise<any>} Parsed JSON response.
 */
export async function httpRequestJson({
  url,
  method = "GET",
  headers = {},
  params = null,
  data = null,
} = {}) {
  if (isIosCapacitor()) {
    // --- Native path (iOS): uses URLSession via CapacitorHttp ---
    const options = {
      url,
      method,
      headers: { "Content-Type": "application/json", ...headers },
      responseType: "json",
    };
    if (params && Object.keys(params).length > 0) {
      options.params = params;
    }
    if (data != null) {
      options.data = data;
    }

    const response = await CapacitorHttp.request(options);

    if (response.status < 200 || response.status >= 300) {
      throw httpJsonError(response.status, response.data);
    }

    // CapacitorHttp already parses JSON when responseType="json".
    return response.data;
  } else {
    // --- Web/Android path: standard fetch ---
    let finalUrl = url;
    if (params && Object.keys(params).length > 0) {
      const qs = new URLSearchParams(params).toString();
      finalUrl = `${url}?${qs}`;
    }

    const fetchOptions = {
      method,
      headers: { "Content-Type": "application/json", ...headers },
    };
    if (data != null) {
      fetchOptions.body = JSON.stringify(data);
    }

    const response = await fetch(finalUrl, fetchOptions);
    if (!response.ok) {
      const text = await response.text();
      throw httpJsonError(response.status, text);
    }
    return response.json();
  }
}
